import { UtensilsCrossed } from "lucide-react";
import { API } from "../../App";

/**
 * Public menu rendering for a venue profile page. Lifted out of
 * `VenueDetailPage.jsx` (was lines 721-772). Receives the categorised menu
 * payload exactly as the `/api/venues/{id}/menu` endpoint returns it.
 *
 * @param {{name?:string, name_el?:string, items?:any[]}[]} categories
 * @param {"en"|"el"} lang
 * @param {number}    order   CSS flex `order` for layout-driven section sort
 */
export default function VenueMenu({ categories, lang, order }) {
  if (!categories || categories.length === 0) return null;

  return (
    <div data-testid="venue-menu" style={{ order }}>
      <h2 className="text-xl font-medium text-foreground mb-4 flex items-center gap-2">
        <UtensilsCrossed className="h-5 w-5 text-primary" />
        Menu
      </h2>
      <div className="space-y-6">
        {categories.map((cat, catIdx) => (
          <div key={cat.category_id || cat.name || catIdx}>
            {cat.name && (
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                {lang === "el" ? cat.name_el || cat.name : cat.name}
              </h3>
            )}
            <div className="space-y-2">
              {(cat.items || []).map((item) => (
                <MenuItem key={item.item_id || item.name} item={item} lang={lang} catIdx={catIdx} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MenuItem({ item, lang, catIdx }) {
  const imgUrl = item.image_url
    ? item.image_url.startsWith("http")
      ? item.image_url
      : `${API.replace("/api", "")}${item.image_url}`
    : null;
  const itemName = lang === "el" ? item.name_el || item.name : item.name;
  const itemDesc = lang === "el" ? item.description_el || item.description : item.description;
  return (
    <div
      className="flex items-center gap-3 py-2"
      data-testid={`menu-public-item-${item.item_id || catIdx}`}
    >
      {imgUrl && (
        <img
          src={imgUrl}
          alt={itemName}
          className="w-12 h-12 rounded-lg object-cover shrink-0"
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-medium text-foreground text-sm">{itemName}</span>
          {item.price != null && (
            <span className="text-sm font-semibold text-foreground shrink-0 tabular-nums">
              {"\u20AC"}
              {Number(item.price).toFixed(2)}
            </span>
          )}
        </div>
        {itemDesc && (
          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{itemDesc}</p>
        )}
        {item.dietary_tags?.length > 0 && (
          <div className="flex gap-1 mt-1 flex-wrap">
            {item.dietary_tags.map((tag) => (
              <span
                key={tag}
                className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
